/* =====================================================================
   Artificial Living, composite fencing product page: motion and colour (Oct 2026).
   Loaded with defer by sections/fence-planner.liquid, beside al-fencing-pdp.css. No libraries.

   Everything here is an enhancement. With this file missing, failing or under reduced motion, every
   section is complete, visible and still:
   - Nothing is hidden in the markup or the stylesheet. An element is only hidden ("armed") here, by
     this script, when it is still below the fold and motion is allowed; it is revealed once, when it
     scrolls into view, and the classes are then removed.
   - The colour gallery's swatches stay hidden until this script shows them, so without it the gallery
     is one photograph and its colour name.

   1. Reveal on scroll: [data-fx-rv] fades and rises, [data-fx-stagger] does the same to its children in
      turn, [data-fx-draw] draws an SVG's strokes then fades its fills and labels in, [data-fx-count]
      counts its first number up, [data-fx-zoom] settles a photo band. One IntersectionObserver for all of them.
      The delivery steps ([data-fx-ship]) have their own: a green pill travels the rail once and each step comes
      alive as it arrives.
   2. Colour: the gallery ([data-fx-colours]), the page's own colour swatches and the colour-aware
      drawings ([data-fx-board]) follow one choice.
   3. Reviews carousel ([data-fx-carousel]): arrows and a progress bar over a scroll-snap row.
   4. Highlight drawings ([data-fx-art]): each card's drawing plays once when the card comes into view, whether
      the page or the row is scrolled; [data-fx-hl-count] figures count up with it.
   ===================================================================== */
(() => {
  'use strict';
  if (window.__alFencePdp) return;
  window.__alFencePdp = true;

  const doc = document;
  const reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  const motionAllowed = () => !reduceQuery.matches && 'IntersectionObserver' in window;
  const norm = (value) => String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');

  /* ---------- 1. Reveal on scroll ---------- */

  let observer = null;
  const onReveal = new WeakMap();
  const STAGGER_MS = 90;
  const STAGGER_CAP = 9;

  function belowFold(el) {
    return el.getBoundingClientRect().top > window.innerHeight * 0.9;
  }

  /** Hide an element now and reveal it when it scrolls into view; tidy its classes away afterwards. */
  function arm(el, kind, settleMs, hook) {
    el.classList.add(kind, 'fx-armed');
    onReveal.set(el, { kind, settleMs, hook });
    observer.observe(el);
  }

  function reveal(el) {
    observer.unobserve(el);
    const entry = onReveal.get(el);
    el.classList.add('is-in');
    if (!entry) return;
    if (entry.hook) entry.hook(el);
    window.setTimeout(() => {
      el.classList.remove('fx-armed', 'is-in', entry.kind);
    }, entry.settleMs);
  }

  function revealAll() {
    if (!observer) return;
    doc.querySelectorAll('.fx-armed:not(.is-in)').forEach(reveal);
  }

  /** Measures each stroke for the draw-in, and orders shapes so they build in document order. */
  function prepareDrawing(svg) {
    let index = 0;
    svg.querySelectorAll('path, rect, circle, line, polyline, polygon, ellipse, text').forEach((shape) => {
      if (shape.closest('defs, pattern, clipPath, mask')) return;
      const style = window.getComputedStyle(shape);
      shape.style.setProperty('--fx-i', String(Math.min(index, 28)));
      index += 1;
      if (shape.tagName.toLowerCase() === 'text') {
        shape.classList.add('fx-dr-t');
        return;
      }
      const dashed = style.strokeDasharray && style.strokeDasharray !== 'none';
      if (dashed) {
        /* Dashed guides already carry a dash pattern, so they fade instead of drawing. */
        shape.classList.add('fx-dr-t');
        return;
      }
      const stroked = style.stroke && style.stroke !== 'none' && parseFloat(style.strokeWidth) > 0;
      if (stroked && typeof shape.getTotalLength === 'function') {
        let length = 0;
        try { length = shape.getTotalLength(); } catch (error) { length = 0; }
        if (length > 0) {
          shape.style.setProperty('--fx-len', `${Math.ceil(length) + 2}px`);
          shape.classList.add('fx-dr-s');
        }
      }
      if (style.fill && style.fill !== 'none') shape.classList.add('fx-dr-f');
    });
  }

  /** Counts the first number in an element up from zero; the finished text is put back exactly. */
  function prepareCount(el) {
    const finalText = el.textContent;
    const match = finalText.match(/^(\D*?)(\d+(?:\.\d+)?)([\s\S]*)$/);
    if (!match) return null;
    const target = parseFloat(match[2]);
    if (!(target > 0)) return null;
    const decimals = (match[2].split('.')[1] || '').length;
    const render = (value) => { el.textContent = `${match[1]}${value.toFixed(decimals)}${match[3]}`; };
    render(0);
    return () => {
      const start = performance.now();
      const duration = 1000;
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        if (t >= 1) {
          el.textContent = finalText;
          return;
        }
        render(target * (1 - Math.pow(1 - t, 3)));
        window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    };
  }

  function initReveal() {
    if (!motionAllowed()) return;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) reveal(entry.target); });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    doc.querySelectorAll('[data-fx-rv]').forEach((el) => {
      if (belowFold(el)) arm(el, 'fx-rv', 1500);
    });
    doc.querySelectorAll('[data-fx-stagger]').forEach((el) => {
      if (!belowFold(el)) return;
      const children = Array.from(el.children);
      children.forEach((child, i) => child.style.setProperty('--fx-i', String(Math.min(i, STAGGER_CAP))));
      arm(el, 'fx-stg', 1200 + STAGGER_MS * Math.min(children.length, STAGGER_CAP + 1));
    });
    doc.querySelectorAll('svg[data-fx-draw]').forEach((svg) => {
      if (!belowFold(svg)) return;
      prepareDrawing(svg);
      arm(svg, 'fx-draw', 3200);
    });
    doc.querySelectorAll('[data-fx-count]').forEach((el) => {
      if (!belowFold(el)) return;
      const run = prepareCount(el);
      if (run) arm(el, 'fx-cnt', 50, run);
    });
    doc.querySelectorAll('[data-fx-zoom]').forEach((el) => {
      if (belowFold(el)) arm(el, 'fx-zoom', 2800);
    });

    window.addEventListener('beforeprint', revealAll);
  }

  /* The delivery steps (sections/fencing-delivery.liquid). The rail runs from the first step's dot to the last,
     measured, so it fits whether the steps sit in a row (desktop) or a column (phones). When the section comes into
     view a green pill travels the rail once and each step comes alive as the pill reaches its dot. Under reduced
     motion, or if anything fails, the full green rail and every step are shown, still. */
  function initShip() {
    doc.querySelectorAll('[data-fx-ship]').forEach((flow) => {
      const steps = Array.from(flow.querySelectorAll('.fx-ship__step'));
      const nodes = steps.map((step) => step.querySelector('.fx-ship__node'));
      if (steps.length < 2 || nodes.some((node) => !node)) return;
      let fractions = steps.map(() => 0);
      const measure = () => {
        const box = flow.getBoundingClientRect();
        const centre = (node) => {
          const rect = node.getBoundingClientRect();
          return { x: rect.left + rect.width / 2 - box.left, y: rect.top + rect.height / 2 - box.top };
        };
        const points = nodes.map(centre);
        const first = points[0];
        const last = points[points.length - 1];
        const across = Math.abs(last.y - first.y) < 2;
        const length = Math.max(0, across ? last.x - first.x : last.y - first.y);
        flow.style.setProperty('--fx-x', `${first.x}px`);
        flow.style.setProperty('--fx-y', `${first.y}px`);
        flow.style.setProperty('--fx-len', `${length}px`);
        flow.classList.toggle('is-across', across);
        flow.classList.add('has-track');
        fractions = points.map((point) => (length ? Math.min(1, Math.max(0, (across ? point.x - first.x : point.y - first.y) / length)) : 0));
      };
      measure();
      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(flow);
      else window.addEventListener('resize', measure);
      if (!motionAllowed()) return;

      const finish = () => {
        flow.style.setProperty('--fx-p', '1');
        steps.forEach((step) => step.classList.add('is-on'));
        flow.classList.add('is-done');
      };
      flow.style.setProperty('--fx-p', '0');
      flow.classList.add('fx-ship-armed');
      const run = () => {
        const duration = 2800;
        const started = performance.now();
        flow.classList.add('is-run');
        const tick = (now) => {
          const t = Math.min(1, (now - started) / duration);
          const p = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          flow.style.setProperty('--fx-p', p.toFixed(4));
          steps.forEach((step, index) => { if (p >= fractions[index] - 0.002) step.classList.add('is-on'); });
          if (t < 1) window.requestAnimationFrame(tick);
          else finish();
        };
        window.requestAnimationFrame(tick);
      };
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        run();
      }, { rootMargin: '0px 0px -18% 0px', threshold: 0.35 });
      io.observe(flow);
      window.addEventListener('beforeprint', () => { io.disconnect(); finish(); });
    });
  }

  /* ---------- 2. Colour ---------- */

  let colourIndex = -1;
  let currentColour = '';
  const hexByName = new Map();
  const galleries = [];
  let pendingRestore = null;

  function productInfo() {
    return doc.querySelector('[id^="shopify-section-template--"] product-information') || doc.querySelector('product-information');
  }

  function pageColourInputs() {
    const info = productInfo();
    if (!info || colourIndex < 0) return [];
    return Array.from(info.querySelectorAll(`product-swatch[data-option-index="${colourIndex}"] input[data-product-variant]`));
  }

  function pageColour() {
    const checked = pageColourInputs().find((input) => input.checked);
    return checked ? checked.value : '';
  }

  /** Board colour in the drawings, and the colour named in their captions. */
  function paintDrawings(value) {
    const hex = hexByName.get(norm(value));
    if (!hex) return;
    doc.querySelectorAll('[data-fx-board]').forEach((el) => el.style.setProperty('--fx-board', hex));
    doc.querySelectorAll('[data-fx-colour-name]').forEach((el) => { el.textContent = value; });
  }

  function setColour(value, source) {
    if (!value) return;
    if (norm(value) === norm(currentColour) && source !== 'init') return;
    currentColour = value;
    galleries.forEach((gallery) => gallery.show(value));
    paintDrawings(value);
  }

  /** Selects the page's own colour swatch, exactly as a shopper's click would, so price, photos and the planner follow. */
  function pushToPage(value, anchor) {
    const target = pageColourInputs().find((input) => norm(input.value) === norm(value));
    if (!target || target.checked) return;
    pendingRestore = { el: anchor, top: anchor.getBoundingClientRect().top, at: Date.now() };
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* After a variant swap the theme focuses its own checked swatch, which scrolls the page up to it.
     Put focus and scroll back on the gallery the shopper was using. */
  function restoreAfterVariantChange() {
    const pending = pendingRestore;
    pendingRestore = null;
    if (!pending || Date.now() - pending.at > 8000 || !pending.el.isConnected) return;
    window.setTimeout(() => {
      pending.el.focus({ preventScroll: true });
      const drift = pending.el.getBoundingClientRect().top - pending.top;
      if (Math.abs(drift) > 1) window.scrollBy(0, drift);
    }, 0);
  }

  function initGallery(root) {
    const picker = root.querySelector('[data-fx-cols-pick]');
    const nameEl = root.querySelector('[data-fx-cols-name]');
    const inputs = Array.from(root.querySelectorAll('input[data-fx-cols-value]'));
    const images = Array.from(root.querySelectorAll('[data-fx-cols-img]'));
    if (!picker || !inputs.length || !images.length) return;
    const index = parseInt(root.getAttribute('data-colour-index'), 10);
    if (index >= 0) colourIndex = index;
    const syncPage = root.hasAttribute('data-sync-page');
    inputs.forEach((input) => {
      const hex = input.getAttribute('data-hex');
      if (hex) hexByName.set(norm(input.value), hex);
    });
    picker.hidden = false;

    const imageFor = (value) => images.find((img) => norm(img.getAttribute('data-fx-cols-img')) === norm(value));
    /* Hidden photos are lazy and so not fetched; un-hiding one (still transparent) starts its download. */
    const warm = (value) => {
      const img = imageFor(value);
      if (img && img.hidden) img.hidden = false;
    };
    let token = 0;

    async function show(value) {
      const input = inputs.find((item) => norm(item.value) === norm(value));
      if (!input) return;
      if (!input.checked) input.checked = true;
      if (nameEl) nameEl.textContent = input.value;
      const img = imageFor(value);
      if (!img || img.classList.contains('is-current')) return;
      const mine = ++token;
      img.hidden = false;
      if (!img.complete && typeof img.decode === 'function') {
        try { await img.decode(); } catch (error) { /* show whatever arrived */ }
      }
      if (mine !== token) return;
      images.forEach((other) => {
        const isNew = other === img;
        other.classList.toggle('is-prev', other.classList.contains('is-current') && !isNew);
        other.classList.toggle('is-current', isNew);
        other.setAttribute('aria-hidden', isNew ? 'false' : 'true');
      });
      window.setTimeout(() => {
        if (mine === token) images.forEach((other) => other.classList.remove('is-prev'));
      }, 700);
    }

    picker.addEventListener('change', (event) => {
      const input = event.target.closest('input[data-fx-cols-value]');
      if (!input) return;
      setColour(input.value, 'gallery');
      if (syncPage) pushToPage(input.value, input);
    });
    picker.addEventListener('pointerover', (event) => {
      const label = event.target.closest('[data-value]');
      if (label) warm(label.getAttribute('data-value'));
    });
    picker.addEventListener('focusin', (event) => {
      const input = event.target.closest('input[data-fx-cols-value]');
      if (input) warm(input.value);
    });
    picker.addEventListener('touchstart', (event) => {
      const label = event.target.closest('[data-value]');
      if (label) warm(label.getAttribute('data-value'));
    }, { passive: true });

    galleries.push({ show });
  }

  function initColour() {
    doc.querySelectorAll('[data-fx-colours]').forEach(initGallery);
    if (colourIndex < 0) return;

    /* The page's own swatches (a shopper's click, or the planner's board colours) lead everything else. */
    doc.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('input[data-product-variant]')) return;
      const swatch = input.closest('product-swatch');
      if (!swatch || String(swatch.getAttribute('data-option-index')) !== String(colourIndex)) return;
      if (input.closest('product-information') !== productInfo()) return;
      setColour(input.value, 'page');
    });

    let subscribed = false;
    const subscribe = () => {
      const bus = window.eventBus;
      if (subscribed || !bus || typeof bus.on !== 'function') return;
      subscribed = true;
      bus.on('variant:updated', (event) => {
        const info = productInfo();
        if (event && event.sectionId && info && info.getAttribute('data-section-id') && event.sectionId !== info.getAttribute('data-section-id')) return;
        if (event && event.variant && Array.isArray(event.variant.options)) setColour(event.variant.options[colourIndex], 'page');
        restoreAfterVariantChange();
      });
    };
    /* The theme's event bus is a deferred module: subscribe now if it is there, else once the page has loaded. */
    subscribe();
    doc.addEventListener('DOMContentLoaded', subscribe, { once: true });
    window.addEventListener('load', subscribe, { once: true });

    const start = pageColour();
    const fallback = galleries.length ? (doc.querySelector('[data-fx-colours] input[data-fx-cols-value]:checked') || {}).value : '';
    setColour(start || fallback, 'init');
  }

  /* ---------- 3. Reviews carousel ---------- */

  function initCarousel(root) {
    const row = root.querySelector('[data-fx-car-row]');
    const nav = root.querySelector('[data-fx-car-nav]');
    const prev = root.querySelector('[data-fx-car-prev]');
    const next = root.querySelector('[data-fx-car-next]');
    const bar = root.querySelector('[data-fx-car-bar]');
    if (!row || !nav || !prev || !next) return;
    nav.hidden = false;

    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      const x = Math.abs(row.scrollLeft);
      prev.setAttribute('aria-disabled', x <= 2 ? 'true' : 'false');
      next.setAttribute('aria-disabled', x >= max - 2 ? 'true' : 'false');
      nav.classList.toggle('is-static', max <= 2);
      if (bar && row.scrollWidth > 0) {
        bar.style.setProperty('--fx-car-w', String(Math.min(1, row.clientWidth / row.scrollWidth)));
        bar.style.setProperty('--fx-car-x', String(max > 0 ? Math.min(1, x / max) : 0));
      }
    };
    const step = (direction) => {
      const card = row.querySelector(':scope > li:not([hidden])');
      const gap = parseFloat(window.getComputedStyle(row).columnGap) || 0;
      const width = card ? card.getBoundingClientRect().width + gap : row.clientWidth;
      const perView = Math.max(1, Math.floor((row.clientWidth + gap) / width));
      row.scrollBy({ left: direction * width * perView, behavior: reduceQuery.matches ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', () => { if (prev.getAttribute('aria-disabled') !== 'true') step(-1); });
    next.addEventListener('click', () => { if (next.getAttribute('aria-disabled') !== 'true') step(1); });
    let ticking = false;
    row.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => { ticking = false; update(); });
    }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(row);
    else window.addEventListener('resize', update);
    update();
  }

  /* ---------- 4. Highlight drawings ---------- */

  /** Counts an element's number up from zero after a delay; the finished text is put back exactly. */
  function prepareHlCount(el) {
    const finalText = el.textContent;
    const match = finalText.match(/^(\D*?)(\d+)([\s\S]*)$/);
    if (!match || !(parseInt(match[2], 10) > 0)) return null;
    const target = parseInt(match[2], 10);
    const delay = parseInt(el.getAttribute('data-fx-hl-delay'), 10) || 0;
    const duration = parseInt(el.getAttribute('data-fx-hl-dur'), 10) || 1000;
    el.textContent = `${match[1]}0${match[3]}`;
    return () => {
      window.setTimeout(() => {
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          if (t >= 1) { el.textContent = finalText; return; }
          el.textContent = `${match[1]}${Math.round(target * (1 - Math.pow(1 - t, 2)))}${match[3]}`;
          window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
      }, delay);
    };
  }

  function initHighlights() {
    const cards = Array.from(doc.querySelectorAll('[data-fx-art]'));
    if (!cards.length || !motionAllowed()) return;
    const counters = new WeakMap();
    const play = (card) => {
      card.classList.add('is-play');
      (counters.get(card) || []).forEach((run) => run());
    };
    /* The viewport is the root, so a card hidden in the row's scroll box only counts once it is scrolled into view. */
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        play(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });
    cards.forEach((card) => {
      counters.set(card, Array.from(card.querySelectorAll('[data-fx-hl-count]')).map(prepareHlCount).filter(Boolean));
      card.classList.add('fx-art-armed');
      io.observe(card);
    });
    /* Printing shows every drawing finished. */
    window.addEventListener('beforeprint', () => cards.forEach((card) => { if (!card.classList.contains('is-play')) play(card); }));
  }

  /* ---------- Start ---------- */

  const parts = [
    ['colour', initColour],
    ['carousel', () => doc.querySelectorAll('[data-fx-carousel]').forEach(initCarousel)],
    ['delivery', initShip],
    ['highlights', initHighlights],
    ['reveal', initReveal]
  ];
  const start = () => {
    parts.forEach(([name, run]) => {
      try {
        run();
      } catch (error) {
        if (name === 'reveal') {
          /* Never leave anything hidden: undo every armed element at once. */
          doc.querySelectorAll('.fx-armed').forEach((el) => el.classList.remove('fx-armed'));
        }
        if (name === 'highlights') doc.querySelectorAll('.fx-art-armed').forEach((el) => el.classList.add('is-play'));
        if (name === 'delivery') doc.querySelectorAll('.fx-ship-armed').forEach((el) => el.classList.remove('fx-ship-armed'));
        if (window.console) window.console.error(`Fencing page: ${name} could not start`, error);
      }
    });
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
